import "./Calculator.css";

import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import parse from "html-react-parser";
import axios from "axios";
import { isEmpty } from "lodash";

import useStore from "stores/Store";
import { calculatorUmrah, CustomSlider, marks } from "constants/calculator";

const CalculatorUmrah = ({ onIncomeRangeChange }) => {
  const [incomeRange, setIncomeRange] = useState("below5k");
  const [financingAmount, setFinancingAmount] = useState("");
  const [tenure, setTenure] = useState(1);
  const [monthlyPayment, setMonthlyPayment] = useState("0.00");
  const [isActive, setIsActive] = useState(false);
  const [amountError, setAmountError] = useState("");
  const [isCalculated, setIsCalculated] = useState(false);
  const minTenure = incomeRange === "below5k" ? 1 : 2;
  const maxTenure = 8;
  const [incomeLow, setIncomeLow] = useState({});
  const [incomeHigh, setIncomeHigh] = useState({});
  const { calculatorState, setCalculatorState } = useStore();

  const handleIncomeChange = (range) => {
    setIncomeRange(range);
    setTenure(
      range === "below5k"
        ? parseInt(Object.keys(incomeLow)[0])
        : parseInt(Object.keys(incomeHigh)[0])
    );
    setFinancingAmount("");
    setMonthlyPayment("0.00");
    setAmountError("");
    onIncomeRangeChange(range);
  };

  const roundToNearest100 = (value) => Math.round(value / 100) * 100;

  const handleFinancingAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");
    setFinancingAmount(value);
    setAmountError("");
  };

  const calculateTotal = () => {
    const minAmount = incomeRange === "below5k" ? 10000 : 5000;
    const maxAmount = incomeRange === "below5k" ? 50000 : 250000;

    let amount = parseFloat(financingAmount);
    if (isNaN(amount) || financingAmount === "") {
      setAmountError(calculatorUmrah.amountErrorRequired);
      return;
    } else if (amount < minAmount || amount > maxAmount) {
      setAmountError(
        incomeRange === "below5k"
          ? calculatorUmrah.amountErrorRangeBelow5k
          : calculatorUmrah.amountErrorRangeAbove5k
      );
      return;
    } else {
      amount = roundToNearest100(amount);
    }

    // const repaymentRates =
    //   incomeRange === "below5k"
    //     ? {
    //         1: 5.78 / 100,
    //         2: 6.3 / 100,
    //         3: 8.47 / 100,
    //         4: 8.68 / 100,
    //         5: 8.94 / 100,
    //         6: 9.23 / 100,
    //         7: 9.46 / 100,
    //         8: 9.76 / 100,
    //       }
    //     : {
    //         1: 4 / 100,
    //         2: 5.94 / 100,
    //         3: 8.18 / 100,
    //         4: 8.39 / 100,
    //         5: 8.63 / 100,
    //         6: 8.87 / 100,
    //         7: 9.13 / 100,
    //         8: 9.38 / 100,
    //       };
    const repaymentRates = incomeRange === "below5k" ? incomeLow : incomeHigh;

    const rate = repaymentRates[tenure];
    const total = (amount * rate * tenure + amount) / (tenure * 12);

    setMonthlyPayment(
      parseFloat(total).toLocaleString("ms-MY", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );

    setFinancingAmount(amount.toString());
    setIsCalculated(true);
    setAmountError("");
  };

  useEffect(() => {
    if (isCalculated) {
      calculateTotal();
    }
  }, [tenure]);

  const handleAssign = (data) => {
    const incomeLowData = data.PersonalFinancingIUmrah[0]?.incomeLowRange;
    const incomeHighData = data.PersonalFinancingIUmrah[0]?.incomeHighRange;

    // Transform the array into an object with { item.tenure: item.rate }
    const incomeLowObject = incomeLowData?.reduce((acc, item) => {
      acc[item.tenure] = item.rate / 100;
      return acc;
    }, {});
    const incomeHighObject = incomeHighData?.reduce((acc, item) => {
      acc[item.tenure] = item.rate / 100;
      return acc;
    }, {});

    // Set the state with the transformed object
    setIncomeLow(incomeLowObject);
    setIncomeHigh(incomeHighObject);
    setTenure(
      incomeRange === "below5k"
        ? parseInt(Object.keys(incomeLowObject)[0])
        : parseInt(Object.keys(incomeHighObject)[0])
    );
  };

  const fetchPFIUmrahData = async () => {
    // let url = `${process.env.REACT_APP_CMS_BASE_URL}/personal-financings?populate[PersonalFinancingIUmrah][populate]=*`;
    let url = `${process.env.REACT_APP_CMS_BASE_URL}/personal-financings?pLevel`;

    try {
      const response = await axios.get(url);
      setCalculatorState(response.data.data[0]);

      handleAssign(response.data.data[0]);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  useEffect(() => {
    if (isEmpty(calculatorState)) {
      fetchPFIUmrahData();
    } else {
      handleAssign(calculatorState);
    }
  }, []);

  return (
    <div
      id="calculator"
      className="calculator section-calc light-background calculator-form"
    >
      <div data-aos="fade-up" data-aos-delay="200" className="w-100">
        <p className="header">{calculatorUmrah.header}</p>
        <div
          className="btn-group w-100"
          role="group"
          aria-label="Basic radio toggle button group"
        >
          <input
            type="radio"
            className="btn-check"
            name="btnradio"
            id="btnradio1"
            checked={incomeRange === "below5k"}
            onChange={() => handleIncomeChange("below5k")}
          />
          <label
            className="income btn btn-outline-primary col-6"
            htmlFor="btnradio1"
            onClick={() => handleIncomeChange("below5k")}
          >
            {parse(calculatorUmrah.incomeBelow5k)}
          </label>

          <input
            type="radio"
            className="btn-check"
            name="btnradio"
            id="btnradio2"
            checked={incomeRange === "above5k"}
            onChange={() => handleIncomeChange("above5k")}
          />
          <label
            className="income btn btn-outline-primary col-6"
            htmlFor="btnradio2"
            onClick={() => handleIncomeChange("above5k")}
          >
            {parse(calculatorUmrah.incomeAbove5k)}
          </label>
        </div>

        <div className={`form-group-calc ${isActive ? "active" : ""}`}>
          <label
            htmlFor="financing-amount"
            className="income-group income-group-text "
          >
            {incomeRange === "below5k"
              ? calculatorUmrah.financingAmountBelow5k
              : calculatorUmrah.financingAmountAbove5k}
          </label>
          <div className="input-group">
            <span className="amount">RM</span>
            <input
              type="text"
              id="financing-amount"
              name="financing-amount"
              placeholder={calculatorUmrah.insertAmountPlaceholder}
              className={`form-control-input ${
                amountError ? "input-error" : ""
              }`}
              value={financingAmount}
              onChange={handleFinancingAmountChange}
              onFocus={() => setIsActive(true)}
              onBlur={() => setIsActive(false)}
              style={{ border: "none" }}
            />
          </div>
          {amountError && <div className="error-message">{amountError}</div>}
        </div>

        <div className="form-group-2">
          <label htmlFor="tenure" className="tenure">
            {calculatorUmrah.tenureLabel}
          </label>
          <div className="input-group">
            <CustomSlider
              value={tenure}
              onChange={(_, value) => {
                setTenure(value);
                setIsCalculated(true);
              }}
              step={1}
              marks={marks}
              min={
                incomeRange === "below5k"
                  ? parseInt(Object.keys(incomeLow)[0])
                  : parseInt(Object.keys(incomeHigh)[0])
              }
              max={
                incomeRange === "below5k"
                  ? parseInt(
                      Object.keys(incomeLow)[Object.keys(incomeLow).length - 1]
                    )
                  : parseInt(
                      Object.keys(incomeHigh)[
                        Object.keys(incomeHigh).length - 1
                      ]
                    )
              }
              valueLabelDisplay="off"
              aria-label="Financing Tenure"
            />
            {/* <CustomSlider
              value={tenure}
              onChange={(_, value) => {
                setTenure(value);
                setIsCalculated(true);
              }}
              step={1}
              marks={marks}
              min={minTenure}
              max={maxTenure}
              valueLabelDisplay="off"
              aria-label="Financing Tenure"
            /> */}
          </div>
          <p className="payment">{calculatorUmrah.monthlyPaymentLabel}</p>

          <Container>
            <Row className="align-items-center justify-content-between row-cols-2">
              <Col xs={8} className="ps-0">
                <div className="form-group-3">
                  <div className="input-group center-input-group">
                    <span className="input-group-text-2">RM</span>
                    <span className="financing-amount-style">
                      {monthlyPayment}
                    </span>
                  </div>
                </div>
              </Col>
              <Col xs={4} className="pe-0">
                <button
                  type="button"
                  className="btn btn-primary calculate-button w-100"
                  onClick={calculateTotal}
                >
                  {calculatorUmrah.calculateTotalButton}
                </button>
              </Col>
            </Row>
          </Container>
        </div>
        <span className="notes">{calculatorUmrah.notes}</span>
      </div>
    </div>
  );
};

export default CalculatorUmrah;
